import { SortingState } from '@tanstack/react-table';
import { ExpectedQueryData } from 'components/table/hooks/useServerFilteredTable';
import { getPaginationParams } from 'components/table/utils/pagination';
import { getSortParam } from 'components/table/utils/sorting';
import {
  AutomatedReviewLog,
  GetAutomatedReviewLogsResponse,
} from 'models/automation/Reviews';
import { AutomatedReviewLogsFiltersSchemaType } from 'pages/automations/reviews/sections/automatedReviewLogs/filters/AutomatedReviewLogsFilters.schema';
import API from 'services/API';

type QueryReturnType = ExpectedQueryData<AutomatedReviewLog>;

export const AUTOMATED_REVIEW_LOGS_BASE_QUERY_KEY = 'automated-review-logs';

export const AUTOMATED_REVIEW_LOGS_SORTING: Partial<
  Record<
    keyof AutomatedReviewLog & `guest.${string}` & `property.${string}`,
    string
  >
> = {
  reviewDate: 'REVIEW_DATE',
  reviewTemplateTitle: 'REVIEW_TEMPLATE_TITLE',
  'guest.name': 'GUEST_NAME',
  channel: 'CHANNEL',
  'property.name': 'PROPERTY_NAME',
  status: 'STATUS',
  availableAction: 'AVAILABLE_ACTION',
};

export const automatedReviewLogsQuery = ({
  filters,
  pageIndex,
  pageSize,
  leadUid,
  sorting,
  agencyUid,
}: {
  filters?: Partial<AutomatedReviewLogsFiltersSchemaType>;
  pageIndex: number;
  pageSize: number;
  leadUid?: string;
  sorting: SortingState;
  agencyUid: string;
}) => ({
  queryKey: [
    AUTOMATED_REVIEW_LOGS_BASE_QUERY_KEY,
    'list',
    { filters, pageIndex, leadUid, sorting },
  ],
  queryFn: async (): Promise<QueryReturnType> => {
    const sort = getSortParam(sorting, AUTOMATED_REVIEW_LOGS_SORTING);

    const params: Record<string, unknown> = {
      ...getPaginationParams({ pageIndex, pageSize }),
      ...(sort ? { sort: sort.sort, sortDirection: sort.direction } : {}),
    };

    params.agencyUid = agencyUid;

    if (filters?.status) {
      params.status = filters.status;
    }

    if (filters?.channel) {
      params.channel = filters.channel;
    }

    if (filters?.propertyUid) {
      params.propertyUid = filters.propertyUid;
    }

    if (leadUid) {
      params.leadUid = leadUid;
    } else if (filters?.lead && filters.lead.length > 0) {
      params.leadUid = filters.lead[0].uid;
    }

    const data = await API.get<GetAutomatedReviewLogsResponse>(
      '/api/internal/scheduled-hosts-reviews',
      {
        params,
      },
    );

    const { automatedReviewLogs, _metadata } = data.data;

    return {
      data: automatedReviewLogs,
      _metadata,
    };
  },
  enabled: true,
});
